import { useEffect, useMemo } from 'react';
import { useLocation } from 'wouter';
import type { Space } from '../types';

export const LAST_SPACE_KEY = 'markflow-last-space-slug';

// Returns the slug embedded in `/space/:slug/...`, or null when the URL is a
// global route. Pure parse — no IO.
export function spaceSlugFromPath(path: string): string | null {
  if (!path.startsWith('/space/')) return null;
  const rest = path.slice('/space/'.length);
  const slug = rest.split('/')[0] ?? '';
  return slug || null;
}

// Best-effort link into the user's last-active Space, for "enter the app"
// actions that live OUTSIDE the App state tree (landing CTA, "Back to app"
// links on standalone pages) and so can't read `currentSpace`. Mirrors the
// redirect source-of-truth in useCurrentSpace. Falls back to '/', which the
// hook then resolves to the default Space — so a logged-in user lands directly
// on their active Space without the transient '/' bounce.
export function activeSpaceHref(): string {
  try {
    const slug = window.localStorage.getItem(LAST_SPACE_KEY);
    if (slug) return `/space/${slug}`;
  } catch {
    // localStorage unavailable — fall through to '/'
  }
  return '/';
}

interface UseCurrentSpaceArgs {
  spaces: Space[];
  spacesLoaded: boolean;
}

// Active-Space resolver + last-active persistence. The hook does three things:
//   1. Maps the wouter URL → Space object (null when on a global route).
//   2. On first mount, if the URL is exactly `/` AND localStorage has a slug
//      whose Space still exists, redirects to /space/<slug>. Honors the
//      ticket's "redirect to last-active Space on app open" requirement
//      without hijacking explicit `/` navigations later in the session.
//   3. Writes the active slug to localStorage whenever the user navigates to
//      a Space — survives reload, used by step 2 on next session.
export function useCurrentSpace({ spaces, spacesLoaded }: UseCurrentSpaceArgs) {
  const [location, setLocation] = useLocation();

  const currentSlug = spaceSlugFromPath(location);

  const currentSpace = useMemo<Space | null>(() => {
    if (!currentSlug) return null;
    return spaces.find((s) => s.slug === currentSlug) ?? null;
  }, [spaces, currentSlug]);

  // MF-194 — `/` no longer has a global "All bookmarks" view. Redirect order:
  //   1. last-active slug from localStorage (if the Space still exists)
  //   2. user's default Space (is_default = true)
  //   3. first Space in the user's sort order (fallback for legacy users
  //      whose backfill skipped them — should be impossible after the
  //      migration but keeps the redirect total)
  // Gated on (a) location is exactly '/' AND (b) the spaces list is loaded —
  // otherwise we'd race the API.
  //
  // Spaces are STICKY: returning to '/' (the Logo, "Back to app" links on
  // standalone pages like /jobs, /settings, /ai-organize) must ALWAYS land the
  // user back on their last-active Space — never silently switch to default.
  // This fires on EVERY '/' navigation (not just first mount): a previous
  // one-shot guard left later "/" navigations stranded, dropping the user on
  // the default Space. Since MF-194 removed the global '/' view there's no
  // reason to ever stay on '/', so unconditional redirect is correct.
  useEffect(() => {
    if (!spacesLoaded) return;
    if (location !== '/') return;
    let saved: string | null = null;
    try {
      saved = window.localStorage.getItem(LAST_SPACE_KEY);
    } catch {
      // localStorage may be unavailable
    }
    const target =
      (saved && spaces.find((s) => s.slug === saved)) ||
      spaces.find((s) => s.isDefault) ||
      spaces[0] ||
      null;
    if (target) setLocation(`/space/${target.slug}`, { replace: true });
  }, [location, spaces, spacesLoaded, setLocation]);

  // Persist whenever the user is inside a Space. We don't clear LS on global
  // navigation — staying global is a transient state, the last-seen Space is
  // the user's home base.
  useEffect(() => {
    if (!currentSpace) return;
    try {
      window.localStorage.setItem(LAST_SPACE_KEY, currentSpace.slug);
    } catch {
      // localStorage may be unavailable
    }
  }, [currentSpace]);

  return { currentSpace, currentSlug };
}
